import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as quranApi from '@/features/quran/api';
import type { SurahView } from '@/features/quran/types';
import type { ApiError } from '@/lib/api/client';

/** Global reference data - not tenant-scoped, so no organization id in the key; a long staleTime since it never changes at runtime. */
export function useSurahs(): UseQueryResult<readonly SurahView[], ApiError> {
  return useQuery({
    queryKey: ['quran', 'surahs'],
    queryFn: () => quranApi.listSurahs(),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Surah number -> its reference row, for the many places that hold a bare
 * `surah` number (every target does) and need to show a name instead.
 *
 * Shares `useSurahs`' cache entry, so the surahs are fetched once for the
 * whole session no matter how many rows render - the reason a list can
 * afford to name every surah without a request per row. Empty until the
 * reference loads; callers fall back to the number, which is always
 * correct even when the name is not available yet.
 */
export function useSurahLookup(): ReadonlyMap<number, SurahView> {
  const { data: surahs } = useSurahs();

  return useMemo(
    () => new Map((surahs ?? []).map((surah) => [surah.number, surah])),
    [surahs],
  );
}
